import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import api from '../utils/api';
import DialogModal from '../components/DialogModal';
import {
  PageHeader,
  FilterBar,
  DataTable,
  Modal,
  FormInput,
  FormSelect,
  StatusBadge
} from '../components/finance';
import { CircleNotch } from '@phosphor-icons/react';
import './ChartOfAccountsDetailPage.css';

const ChartOfAccountsDetailPage = () => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [formData, setFormData] = useState({
    account_code: '',
    account_name: '',
    account_type: 'asset',
    account_category: 'bank_accounts',
    description: '',
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [filterType, setFilterType] = useState('all');
  const [dialogConfig, setDialogConfig] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'warning',
    primaryAction: null,
    secondaryAction: null
  });
  const [pendingDeactivateId, setPendingDeactivateId] = useState(null);

  const accountTypes = ['asset', 'liability', 'equity', 'revenue', 'expense'];
  
  // Map to the exact enum values from the backend
  const accountCategories = {
    asset: [
      { label: 'Bank Accounts', value: 'bank_accounts' },
      { label: 'Accounts Receivable', value: 'accounts_receivable' },
      { label: 'Prepaid Expenses', value: 'prepaid_expenses' },
      { label: 'Fixed Assets', value: 'fixed_assets' }
    ],
    liability: [
      { label: 'Accounts Payable', value: 'accounts_payable' },
      { label: 'Salaries Payable', value: 'salaries_payable' },
      { label: 'Short-term Debt', value: 'short_term_debt' },
      { label: 'Long-term Debt', value: 'long_term_debt' }
    ],
    equity: [
      { label: 'Accumulated Surplus', value: 'accumulated_surplus' },
      { label: 'Retained Earnings', value: 'retained_earnings' }
    ],
    revenue: [
      { label: 'Student Fees', value: 'student_fees' },
      { label: 'Donations', value: 'donations' },
      { label: 'Grants', value: 'grants' },
      { label: 'Other Income', value: 'other_income' }
    ],
    expense: [
      { label: 'Salaries & Wages', value: 'salaries_wages' },
      { label: 'Utilities', value: 'utilities' },
      { label: 'Supplies', value: 'supplies' },
      { label: 'Repairs & Maintenance', value: 'repairs_maintenance' },
      { label: 'Transport Costs', value: 'transport_costs' },
      { label: 'Contracted Services', value: 'contracted_services' },
      { label: 'Depreciation', value: 'depreciation' },
      { label: 'Other Expenses', value: 'other_expenses' }
    ]
  };

  // Convert accountTypes to options format for FormSelect
  const accountTypeOptions = accountTypes.map(type => ({
    label: type.charAt(0).toUpperCase() + type.slice(1),
    value: type
  }));

  // accountCategoryOptions now returns objects directly (already formatted)
  const accountCategoryOptions = (accountCategories[formData.account_type] || []);

  const filterTypeOptions = [
    { label: 'All Types', value: 'all' },
    ...accountTypeOptions
  ];

  useEffect(() => {
    fetchAccounts();
  }, []);

  const fetchAccounts = async () => {
    try {
      setLoading(true);
      const data = await api.get('/api/chart-of-accounts');
      setAccounts(Array.isArray(data) ? data : data.accounts || []);
    } catch (error) {
      toast.error('Failed to fetch accounts');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAccount = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post('/api/chart-of-accounts', formData);
      toast.success('Account created successfully');
      setShowModal(false);
      setFormErrors({});
      setFormData({
        account_code: '',
        account_name: '',
        account_type: 'asset',
        account_category: 'bank_accounts',
        description: '',
      });
      fetchAccounts();
    } catch (error) {
      toast.error(error.message || 'Failed to create account');
    } finally {
      setSubmitting(false);
    }
  };

  const showDialog = (config) => {
    setDialogConfig({
      isOpen: true,
      ...config
    });
  };

  const closeDialog = () => {
    setDialogConfig(prev => ({
      ...prev,
      isOpen: false
    }));
    setPendingDeactivateId(null);
  };

  const handleDeactivate = (accountId) => {
    setPendingDeactivateId(accountId);
    showDialog({
      title: 'Deactivate Account',
      message: 'Are you sure you want to deactivate this account? This action cannot be undone.',
      type: 'warning',
      primaryAction: {
        label: 'Deactivate',
        onClick: async () => {
          closeDialog();
          try {
            await api.delete(`/api/chart-of-accounts/${accountId}`);
            toast.success('Account deactivated');
            fetchAccounts();
          } catch (error) {
            toast.error(error.message || 'Failed to deactivate account');
          }
        }
      },
      secondaryAction: {
        label: 'Cancel',
        onClick: closeDialog
      }
    });
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
      // Reset category when type changes - get the first category's value
      ...(name === 'account_type' && {
        account_category: accountCategories[value]?.[0]?.value || ''
      })
    }));
    // Clear error for this field
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const filteredAccounts = filterType === 'all'
    ? accounts
    : accounts.filter(acc => acc.account_type === filterType);

  // DataTable columns configuration
  const columns = [
    { 
      key: 'account_code', 
      label: 'Account Code',
      render: (value) => <span>{typeof value === 'string' ? value : String(value || '')}</span>
    },
    { 
      key: 'account_name', 
      label: 'Account Name',
      render: (value) => <span>{typeof value === 'string' ? value : String(value || '')}</span>
    },
    {
      key: 'account_type',
      label: 'Type',
      render: (value) => (
        <span style={{
          textTransform: 'capitalize',
          fontWeight: 500,
          fontSize: '12px'
        }}>
          {typeof value === 'string' ? value : String(value || '')}
        </span>
      )
    },
    { 
      key: 'account_category', 
      label: 'Category',
      render: (value) => {
        const categoryStr = typeof value === 'string' ? value : String(value || '');
        return <span style={{ textTransform: 'capitalize' }}>{categoryStr.replace(/_/g, ' ')}</span>;
      }
    },
    { 
      key: 'normal_balance', 
      label: 'Normal Balance',
      render: (value) => <span style={{ textTransform: 'capitalize' }}>{typeof value === 'string' ? value : String(value || '')}</span>
    },
    {
      key: 'is_active',
      label: 'Status',
      render: (value) => {
        const statusText = value ? 'Active' : 'Inactive';
        return <StatusBadge status={statusText} />;
      }
    }
  ];

  // DataTable actions configuration
  const tableActions = [
    {
      label: 'View',
      className: 'primary',
      onClick: (row) => {
        setSelectedAccount(row);
        setShowViewModal(true);
      }
    },
    {
      label: 'Deactivate',
      className: 'danger',
      onClick: (row) => {
        if (row.is_active) {
          handleDeactivate(row.id);
        }
      }
    }
  ];

  // FilterBar configuration
  const filterConfig = [
    {
      name: 'type',
      label: 'Account Type',
      type: 'select',
      value: filterType,
      options: filterTypeOptions
    }
  ];

  return (
    <div className="coa-detail-page">
      <PageHeader
        title="Chart of Accounts Management"
        subtitle="Create and manage your account structure"
        actions={[
          {
            label: 'New Account',
            className: 'primary',
            onClick: () => setShowModal(true)
          }
        ]}
      />

      <FilterBar
        filters={filterConfig}
        onChange={(name, value) => setFilterType(value)}
        onReset={() => setFilterType('all')}
      />

      <DataTable
        columns={columns}
        data={filteredAccounts}
        actions={tableActions}
        loading={loading}
        emptyMessage="No accounts found"
      />

      <Modal
        isOpen={showViewModal}
        onClose={() => {
          setShowViewModal(false);
          setSelectedAccount(null);
        }}
        title="View Account Details"
        actions={[
          {
            label: 'Close',
            className: 'secondary',
            onClick: () => {
              setShowViewModal(false);
              setSelectedAccount(null);
            }
          }
        ]}
      >
        {selectedAccount && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div>
              <label style={{ fontSize: '12px', fontWeight: 600, color: '#666' }}>Account Code</label>
              <p style={{ margin: '8px 0 0 0', fontSize: '14px' }}>{selectedAccount.account_code}</p>
            </div>
            <div>
              <label style={{ fontSize: '12px', fontWeight: 600, color: '#666' }}>Account Name</label>
              <p style={{ margin: '8px 0 0 0', fontSize: '14px' }}>{selectedAccount.account_name}</p>
            </div>
            <div>
              <label style={{ fontSize: '12px', fontWeight: 600, color: '#666' }}>Account Type</label>
              <p style={{ margin: '8px 0 0 0', fontSize: '14px', textTransform: 'capitalize' }}>{selectedAccount.account_type}</p>
            </div>
            <div>
              <label style={{ fontSize: '12px', fontWeight: 600, color: '#666' }}>Category</label>
              <p style={{ margin: '8px 0 0 0', fontSize: '14px', textTransform: 'capitalize' }}>{String(selectedAccount.account_category || '').replace(/_/g, ' ')}</p>
            </div>
            <div>
              <label style={{ fontSize: '12px', fontWeight: 600, color: '#666' }}>Normal Balance</label>
              <p style={{ margin: '8px 0 0 0', fontSize: '14px', textTransform: 'capitalize' }}>{selectedAccount.normal_balance}</p>
            </div>
            <div>
              <label style={{ fontSize: '12px', fontWeight: 600, color: '#666' }}>Status</label>
              <p style={{ margin: '8px 0 0 0', fontSize: '14px' }}>
                <StatusBadge status={selectedAccount.is_active ? 'Active' : 'Inactive'} />
              </p>
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ fontSize: '12px', fontWeight: 600, color: '#666' }}>Description</label>
              <p style={{ margin: '8px 0 0 0', fontSize: '14px' }}>{selectedAccount.description || 'N/A'}</p>
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ fontSize: '12px', fontWeight: 600, color: '#666' }}>Created Date</label>
              <p style={{ margin: '8px 0 0 0', fontSize: '14px' }}>{new Date(selectedAccount.created_at).toLocaleDateString()}</p>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setFormErrors({});
        }}
        title="Create New Account"
        actions={[
          {
            label: submitting ? 'Creating...' : 'Create Account',
            className: 'primary',
            onClick: handleCreateAccount,
            isLoading: submitting
          },
          {
            label: 'Cancel',
            className: 'secondary',
            onClick: () => {
              setShowModal(false);
              setFormErrors({});
            },
            disabled: submitting
          }
        ]}
      >
        <form onSubmit={handleCreateAccount}>
          <FormInput
            label="Account Code"
            name="account_code"
            type="text"
            value={formData.account_code}
            onChange={handleFormChange}
            placeholder="e.g., 1010"
            disabled={submitting}
            required
            error={formErrors.account_code}
          />

          <FormInput
            label="Account Name"
            name="account_name"
            type="text"
            value={formData.account_name}
            onChange={handleFormChange}
            placeholder="e.g., Business Checking Account"
            disabled={submitting}
            required
            error={formErrors.account_name}
          />

          <FormSelect
            label="Account Type"
            name="account_type"
            value={formData.account_type}
            onChange={handleFormChange}
            options={accountTypeOptions}
            disabled={submitting}
            required
          />

          <FormSelect
            label="Category"
            name="account_category"
            value={formData.account_category}
            onChange={handleFormChange}
            options={accountCategoryOptions}
            disabled={submitting}
            required
          />

          <FormInput
            label="Description"
            name="description"
            type="textarea"
            value={formData.description}
            onChange={handleFormChange}
            placeholder="Optional description"
            disabled={submitting}
            rows={3}
          />
        </form>
      </Modal>

      <DialogModal
        isOpen={dialogConfig.isOpen}
        onClose={closeDialog}
        title={dialogConfig.title}
        message={dialogConfig.message}
        type={dialogConfig.type}
        primaryAction={dialogConfig.primaryAction}
        secondaryAction={dialogConfig.secondaryAction}
      />
    </div>
  );
};

export default ChartOfAccountsDetailPage;
